import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import NotFound from "./pages/NotFound";
import ProtectedRoute from "./components/admin/ProtectedRoute";

// Lazy load pages for code splitting
const Homepage = lazy(() => import('./pages/homepage'));
const TourDetail = lazy(() => import('./pages/tour-detail'));
const ContactPage = lazy(() => import('./pages/contact'));
const VehicleRental = lazy(() => import('./pages/vehicle-rental'));
const TourPackages = lazy(() => import('./pages/tour-packages'));
const AboutUs = lazy(() => import('./pages/about-us'));
const PolicyPage = lazy(() => import('./pages/policy/PolicyPage'));

// Admin pages
const AdminLogin = lazy(() => import('./pages/admin/Login'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminTours = lazy(() => import('./pages/admin/Tours'));
const AdminVehicles = lazy(() => import('./pages/admin/Vehicles'));
const AdminInquiries = lazy(() => import('./pages/admin/Inquiries'));
const AdminBookings = lazy(() => import('./pages/admin/Bookings'));
const BookingDetail = lazy(() => import('./pages/admin/BookingDetail'));
const AdminLeads = lazy(() => import('./pages/admin/Leads'));
const LeadDetail = lazy(() => import('./pages/admin/LeadDetail'));
const Settings = lazy(() => import('./pages/admin/Settings'));
const AdminDepartures = lazy(() => import('./pages/admin/Departures'));
const TourForm = lazy(() => import('./pages/admin/TourForm'));
const VehicleForm = lazy(() => import('./pages/admin/VehicleForm'));
const InquiryDetail = lazy(() => import('./pages/admin/InquiryDetail'));
const AdminTourCategories = lazy(() => import('./pages/admin/TourCategories'));
const AdminVehicleCategories = lazy(() => import('./pages/admin/VehicleCategories'));
const AdminCommunications = lazy(() => import('./pages/admin/Communications'));
const AdminAbout = lazy(() => import('./pages/admin/content/AdminAbout'));
const AdminTeam = lazy(() => import('./pages/admin/content/AdminTeam'));
const AdminTestimonials = lazy(() => import('./pages/admin/content/AdminTestimonials'));
const AdminGallery = lazy(() => import('./pages/admin/content/AdminGallery'));

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <RouterRoutes>
            {/* Public Routes */}
            <Route path="/" element={<Homepage />} />
            <Route path="/tour-detail/:slug" element={<TourDetail />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/vehicle-rental" element={<VehicleRental />} />
            <Route path="/tour-packages" element={<TourPackages />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/homepage" element={<Homepage />} />

            {/* Policy Routes */}
            <Route path="/privacy-policy" element={<PolicyPage type="privacy" title="Privacy Policy" />} />
            <Route path="/terms-of-service" element={<PolicyPage type="terms" title="Terms of Service" />} />
            <Route path="/cookie-policy" element={<PolicyPage type="cookies" title="Cookie Policy" />} />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />

            {/* Tours */}
            <Route path="/admin/tours" element={<ProtectedRoute><AdminTours /></ProtectedRoute>} />
            <Route path="/admin/tours/create" element={<ProtectedRoute><TourForm /></ProtectedRoute>} />
            <Route path="/admin/tours/:id/edit" element={<ProtectedRoute><TourForm /></ProtectedRoute>} />

            {/* Tour Categories */}
            <Route path="/admin/tour-categories" element={<ProtectedRoute><AdminTourCategories /></ProtectedRoute>} />

            {/* Vehicles */}
            <Route path="/admin/vehicles" element={<ProtectedRoute><AdminVehicles /></ProtectedRoute>} />
            <Route path="/admin/vehicles/create" element={<ProtectedRoute><VehicleForm /></ProtectedRoute>} />
            <Route path="/admin/vehicles/:id/edit" element={<ProtectedRoute><VehicleForm /></ProtectedRoute>} />

            {/* Vehicle Categories */}
            <Route path="/admin/vehicle-categories" element={<ProtectedRoute><AdminVehicleCategories /></ProtectedRoute>} />

            {/* Inquiries */}
            <Route path="/admin/inquiries" element={<ProtectedRoute><AdminInquiries /></ProtectedRoute>} />
            <Route path="/admin/inquiries/:type/:id" element={<ProtectedRoute><InquiryDetail /></ProtectedRoute>} />

            <Route path="/admin/bookings" element={<ProtectedRoute><AdminBookings /></ProtectedRoute>} />
            <Route path="/admin/bookings/:id" element={<ProtectedRoute><BookingDetail /></ProtectedRoute>} />

            {/* Leads */}
            <Route path="/admin/leads" element={<ProtectedRoute><AdminLeads /></ProtectedRoute>} />
            <Route path="/admin/leads/:id" element={<ProtectedRoute><LeadDetail /></ProtectedRoute>} />
            <Route path="/admin/communications" element={<ProtectedRoute><AdminCommunications /></ProtectedRoute>} />
            <Route path="/admin/departures" element={<ProtectedRoute><AdminDepartures /></ProtectedRoute>} />

            {/* Settings */}
            <Route path="/admin/content/about" element={<ProtectedRoute><AdminAbout /></ProtectedRoute>} />
            <Route path="/admin/content/team" element={<ProtectedRoute><AdminTeam /></ProtectedRoute>} />
            <Route path="/admin/content/testimonials" element={<ProtectedRoute><AdminTestimonials /></ProtectedRoute>} />
            <Route path="/admin/content/gallery" element={<ProtectedRoute><AdminGallery /></ProtectedRoute>} />

            <Route path="/admin/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />

            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
